import React, { useState, useEffect, useRef } from 'react';

// ─────────────────────────────────────────────────────────────
// CORE Gemstone Carousel
// Shared by App.js (participant) and Dashboard.js (admin).
// Four 1080 x 1350 slides, exported as a LinkedIn-ready PDF
// and a single cover PNG.
// Art direction: each card is the velvet lining of a jewellery
// box in the stone's own colour, lit from above, with the real
// stone sitting in it. The last slide is a gem certificate.
// ─────────────────────────────────────────────────────────────

const LOGO_SVG = '/core-logo-for-dark-mode.svg';
const W = 1080;
const H = 1350;
const INK = '#F4EFE7';
const SERIF = "'Libre Bodoni', 'Bodoni 72', Didot, Georgia, serif";
const SANS = "'Public Sans', 'Helvetica Neue', Arial, sans-serif";
const FONT_URL = 'https://fonts.googleapis.com/css2?family=Libre+Bodoni:ital,wght@0,400;0,500;0,600;1,400;1,500&family=Public+Sans:wght@400;500;600;700&display=swap';

// Keyed by GEMSTONES[...].gem
// bg = velvet, lift = where the light hits, accent = readable tint of the stone on the velvet
export const GEM_ART = {
  'LABRADORITE':  { img: '/gems/labradorite.webp',  bg: '#0F2230', lift: '#21435A', accent: '#8CC8E2', family: 'Feldspar',          hardness: '6 to 6.5', source: 'Labrador, Canada' },
  'JASPER':       { img: '/gems/jasper.webp',       bg: '#2A120B', lift: '#4E2516', accent: '#EDA078', family: 'Chalcedony',        hardness: '6.5 to 7', source: 'India' },
  'CITRINE':      { img: '/gems/citrine.webp',      bg: '#251A05', lift: '#4C360B', accent: '#F4C752', family: 'Quartz',            hardness: '7',        source: 'Bolivia' },
  'TURQUOISE':    { img: '/gems/turquoise.webp',    bg: '#052523', lift: '#0E4945', accent: '#74DDD3', family: 'Copper aluminium phosphate', hardness: '5 to 6',   source: 'Nishapur, Iran' },
  'LAPIS LAZULI': { img: '/gems/lapis-lazuli.webp', bg: '#0B1537', lift: '#1B2E6B', accent: '#E3C46E', family: 'Lazurite rock',     hardness: '5 to 6',   source: 'Afghanistan' },
  'MOONSTONE':    { img: '/gems/moonstone.webp',    bg: '#17182E', lift: '#2F3056', accent: '#CDCBF6', family: 'Feldspar',          hardness: '6 to 6.5', source: 'Sri Lanka' },
  'ONYX':         { img: '/gems/onyx.webp',         bg: '#1C1A18', lift: '#36322D', accent: '#DCD2C1', family: 'Chalcedony',        hardness: '7',        source: 'Brazil' },
  'SAPPHIRE':     { img: '/gems/sapphire.webp',     bg: '#06193E', lift: '#113276', accent: '#93B9FF', family: 'Corundum',          hardness: '9',        source: 'Kashmir and Sri Lanka' },
  'OPAL':         { img: '/gems/opal.webp',         bg: '#1C1730', lift: '#382E5C', accent: '#CDBBFF', family: 'Hydrated silica',   hardness: '5 to 6.5', source: 'Australia' },
  'OBSIDIAN':     { img: '/gems/obsidian.webp',     bg: '#1B1511', lift: '#372A20', accent: '#D6B892', family: 'Volcanic glass',    hardness: '5 to 5.5', source: 'Mexico' },
  'CARNELIAN':    { img: '/gems/carnelian.webp',    bg: '#2C090C', lift: '#5A141A', accent: '#FF9466', family: 'Chalcedony',        hardness: '6.5 to 7', source: 'India' },
  'ROSE QUARTZ':  { img: '/gems/rose-quartz.webp',  bg: '#2A1119', lift: '#502333', accent: '#F6BCCA', family: 'Quartz',            hardness: '7',        source: 'Brazil' },
  'PERIDOT':      { img: '/gems/peridot.webp',      bg: '#131E07', lift: '#2B4011', accent: '#C6E46C', family: 'Olivine',           hardness: '6.5 to 7', source: 'Pakistan' },
};

const titleCase = s => String(s || '').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
const fade = (hex, a) => {
  const h = hex.replace('#', '');
  const n = parseInt(h, 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
};

const loadScript = (src, flag) => new Promise((resolve, reject) => {
  if (window[flag]) return resolve();
  const s = document.createElement('script');
  s.src = src;
  s.onload = () => { window[flag] = true; resolve(); };
  s.onerror = reject;
  document.body.appendChild(s);
});

const ensureFonts = () => {
  if (!document.querySelector('link[data-core-gem-fonts]')) {
    const l = document.createElement('link');
    l.rel = 'stylesheet';
    l.href = FONT_URL;
    l.setAttribute('data-core-gem-fonts', '1');
    document.head.appendChild(l);
  }
};

const waitForAssets = async (el) => {
  try {
    await Promise.all([
      document.fonts.load(`500 120px 'Libre Bodoni'`),
      document.fonts.load(`italic 400 60px 'Libre Bodoni'`),
      document.fonts.load(`600 30px 'Public Sans'`),
    ]);
    await document.fonts.ready;
  } catch (e) { /* fall back to system fonts */ }
  const imgs = Array.from(el.querySelectorAll('img'));
  await Promise.all(imgs.map(img => (img.complete && img.naturalWidth) ? null : new Promise(r => { img.onload = r; img.onerror = r; })));
  await new Promise(r => setTimeout(r, 120));
};

// The stone, resting on the velvet with a soft contact shadow
const Stone = ({ art, size }) => (
  <div style={{ position: 'relative', width: size, height: size, margin: '0 auto' }}>
    <div style={{
      position: 'absolute', left: '12%', right: '12%', bottom: size * 0.02, height: size * 0.14,
      background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.25) 45%, rgba(0,0,0,0) 72%)',
    }} />
    <img src={art.img} alt="" crossOrigin="anonymous" style={{ position: 'relative', width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
  </div>
);

const Slide = ({ art, index, total, logo, owner, children }) => (
  <div style={{
    width: W, height: H, position: 'relative', overflow: 'hidden', boxSizing: 'border-box',
    background: `radial-gradient(ellipse 78% 52% at 50% 28%, ${art.lift} 0%, ${art.bg} 72%)`,
    backgroundColor: art.bg, color: INK, display: 'flex', flexDirection: 'column',
  }}>
    {/* edge falloff, like a lined box */}
    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, boxShadow: 'inset 0 0 220px rgba(0,0,0,0.45)', pointerEvents: 'none' }} />

    <div style={{ position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '56px 80px 0' }}>
      <img src={logo} alt="CORE by Carnelian" width={222} height={90} style={{ width: 222, height: 90, display: 'block' }} />
      <div style={{ fontFamily: SANS, fontSize: 24, fontWeight: 500, color: fade(INK, 0.6) }}>{index + 1} of {total}</div>
    </div>

    <div style={{ position: 'relative', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '0 80px' }}>
      {children}
    </div>

    <div style={{ position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '0 80px 60px', fontFamily: SANS, fontSize: 24, color: fade(INK, 0.6) }}>
      <span>{owner}</span>
      <span style={{ color: INK, fontWeight: 600 }}>corebycarnelian.com</span>
    </div>
  </div>
);

const buildSlides = ({ gem, art, name, docId, date, logo }) => {
  const gemName = titleCase(gem.gem);
  const firstName = String(name || '').trim().split(/\s+/)[0] || 'Your';
  const nameSize = gemName.length > 9 ? 124 : 156;
  const total = 4;

  const cover = (
    <Slide art={art} logo={logo} owner={name} index={0} total={total}>
      <div style={{ textAlign: 'center', marginTop: -20 }}>
        <Stone art={art} size={520} />
        <div style={{ fontFamily: SANS, fontSize: 32, fontWeight: 500, color: fade(INK, 0.72), marginTop: 36 }}>
          {firstName}'s CORE gemstone is
        </div>
        <div style={{ fontFamily: SERIF, fontSize: nameSize, fontWeight: 500, lineHeight: 1, letterSpacing: '-0.015em', marginTop: 14 }}>
          {gemName}
        </div>
        <div style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 58, fontWeight: 400, color: art.accent, marginTop: 18 }}>
          {gem.title}
        </div>
        <p style={{ fontFamily: SANS, fontSize: 32, lineHeight: 1.45, fontWeight: 500, color: fade(INK, 0.85), maxWidth: 800, margin: '34px auto 0' }}>
          {gem.tagline}
        </p>
      </div>
    </Slide>
  );

  const meaning = (
    <Slide art={art} logo={logo} owner={name} index={1} total={total}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 56 }}>
        <div style={{ fontFamily: SANS, fontSize: 30, fontWeight: 600, color: art.accent }}>What {gemName} says about you</div>
        <div style={{ width: 170, flexShrink: 0 }}><Stone art={art} size={170} /></div>
      </div>
      <p style={{ fontFamily: SERIF, fontSize: 58, lineHeight: 1.28, fontWeight: 400, margin: 0, letterSpacing: '-0.005em' }}>
        {gem.desc}
      </p>
      <div style={{ width: 120, height: 3, background: art.accent, margin: '64px 0 36px' }} />
      <p style={{ fontFamily: SANS, fontSize: 36, lineHeight: 1.4, fontWeight: 600, color: art.accent, margin: 0 }}>
        {gem.quirky}
      </p>
    </Slide>
  );

  const team = (
    <Slide art={art} logo={logo} owner={name} index={2} total={total}>
      <div style={{ fontFamily: SANS, fontSize: 30, fontWeight: 600, color: art.accent, marginBottom: 22 }}>In a team</div>
      <p style={{ fontFamily: SERIF, fontSize: 70, lineHeight: 1.18, fontWeight: 500, margin: 0, letterSpacing: '-0.01em' }}>
        {gem.inTeam}
      </p>
      <div style={{ height: 1, background: fade(INK, 0.18), margin: '80px 0' }} />
      <div style={{ fontFamily: SANS, fontSize: 30, fontWeight: 600, color: art.accent, marginBottom: 22 }}>Your edge</div>
      <p style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 54, lineHeight: 1.3, fontWeight: 400, margin: 0, color: fade(INK, 0.92) }}>
        {gem.edge}
      </p>
    </Slide>
  );

  const rows = [
    ['Issued to', name || 'Not provided'],
    ['Gemstone', gemName],
    ['Archetype', gem.title],
    ['Mineral family', art.family],
    ['Hardness', `${art.hardness} on the Mohs scale`],
    ['Notable source', art.source],
    ['Certificate no.', docId || ''],
    ['Issued on', date || ''],
  ];

  const certificate = (
    <Slide art={art} logo={logo} owner={name} index={3} total={total}>
      <div style={{ border: `1.5px solid ${fade(art.accent, 0.55)}`, padding: 12 }}>
        <div style={{ border: `1px solid ${fade(art.accent, 0.3)}`, padding: '46px 52px 40px', background: 'rgba(0,0,0,0.18)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 26 }}>
            <div>
              <div style={{ fontFamily: SERIF, fontSize: 54, fontWeight: 500, lineHeight: 1.05 }}>Gemstone certificate</div>
              <div style={{ fontFamily: SANS, fontSize: 24, color: fade(INK, 0.62), marginTop: 10 }}>Issued by the CORE assessment</div>
            </div>
            <div style={{ width: 150, flexShrink: 0 }}><Stone art={art} size={150} /></div>
          </div>
          {rows.map(([k, v]) => (
            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 30, padding: '15px 0', borderTop: `1px solid ${fade(INK, 0.14)}`, fontFamily: SANS }}>
              <span style={{ fontSize: 24, color: fade(INK, 0.6) }}>{k}</span>
              <span style={{ fontSize: 26, fontWeight: 600, textAlign: 'right' }}>{v}</span>
            </div>
          ))}
        </div>
      </div>
      <p style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 50, lineHeight: 1.25, color: art.accent, margin: '56px 0 0' }}>
        {gem.oneLine}
      </p>
      <p style={{ fontFamily: SANS, fontSize: 30, fontWeight: 500, color: fade(INK, 0.8), margin: '22px 0 0' }}>
        Every professional has a gemstone. Take the CORE assessment to find yours.
      </p>
    </Slide>
  );

  return [cover, meaning, team, certificate];
};

export default function GemstoneCarousel({ gem, name, docId, date, T }) {
  const [slide, setSlide] = useState(0);
  const [busy, setBusy] = useState('');
  const [scale, setScale] = useState(0.3889);
  const [logo, setLogo] = useState(LOGO_SVG);
  const frameRef = useRef(null);
  const exportRef = useRef(null);

  useEffect(() => { ensureFonts(); }, []);
  // The logo SVG has no intrinsic size, which html2canvas mis-crops. Rasterise it once.
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      try {
        const c = document.createElement('canvas');
        c.width = 592; c.height = 240;
        c.getContext('2d').drawImage(img, 0, 0, 592, 240);
        setLogo(c.toDataURL('image/png'));
      } catch (e) { /* keep the SVG */ }
    };
    img.src = LOGO_SVG;
  }, []);
  useEffect(() => {
    const el = frameRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(([e]) => setScale(e.contentRect.width / W));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const art = GEM_ART[gem?.gem] || GEM_ART['PERIDOT'];
  const slides = buildSlides({ gem, art, name, docId, date, logo });
  const total = slides.length;
  const go = i => setSlide(((i % total) + total) % total);
  const fileBase = `${String(name || 'CORE').trim().replace(/\s+/g, '_')}_${titleCase(gem.gem).replace(/\s+/g, '_')}`;

  const capture = async (i) => {
    const node = exportRef.current?.children[i];
    if (!node) return null;
    return window.html2canvas(node, { scale: 2, useCORS: true, backgroundColor: art.bg, logging: false });
  };

  const prepare = async () => {
    await loadScript('https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js', '__h2c_ready');
    await waitForAssets(exportRef.current);
  };

  const downloadPDF = async () => {
    if (busy) return;
    setBusy('pdf');
    try {
      await prepare();
      await loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js', '__jspdf_ready');
      const { jsPDF } = window.jspdf;
      const pdf = new jsPDF({ unit: 'px', format: [W, H], orientation: 'portrait' });
      for (let i = 0; i < total; i++) {
        const canvas = await capture(i);
        if (!canvas) continue;
        if (i > 0) pdf.addPage([W, H], 'portrait');
        pdf.addImage(canvas.toDataURL('image/jpeg', 0.94), 'JPEG', 0, 0, W, H);
      }
      pdf.save(`${fileBase}_CORE_Carousel.pdf`);
    } catch (e) {
      alert('The carousel could not be generated. Check your connection and try again.');
    }
    setBusy('');
  };

  const downloadCover = async () => {
    if (busy) return;
    setBusy('png');
    try {
      await prepare();
      const canvas = await capture(0);
      const a = document.createElement('a');
      a.href = canvas.toDataURL('image/png');
      a.download = `${fileBase}_CORE_Cover.png`;
      a.click();
    } catch (e) {
      alert('The image could not be generated. Check your connection and try again.');
    }
    setBusy('');
  };

  const muted = T?.t2 || '#8A8A8A';
  const arrow = {
    position: 'absolute', top: '50%', transform: 'translateY(-50%)', width: 38, height: 38, borderRadius: '50%',
    background: 'rgba(0,0,0,0.45)', border: '1px solid rgba(255,255,255,0.25)', color: '#fff', cursor: 'pointer',
    fontSize: 20, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3,
  };
  const btn = (primary) => ({
    padding: '15px 28px', borderRadius: 999, cursor: busy ? 'wait' : 'pointer', fontFamily: SANS, fontSize: 14, fontWeight: 700,
    border: primary ? 'none' : `1.5px solid ${art.accent}`, background: primary ? art.accent : 'transparent',
    color: primary ? art.bg : (T?.t0 || '#fff'), opacity: busy && busy !== (primary ? 'pdf' : 'png') ? 0.5 : 1,
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div style={{ textAlign: 'center', marginBottom: 22, maxWidth: 520 }}>
        <h2 style={{ fontFamily: SERIF, fontSize: '2rem', fontWeight: 500, color: T?.t0 || INK, margin: 0 }}>Share your gemstone</h2>
        <p style={{ fontFamily: SANS, color: muted, fontSize: 14, lineHeight: 1.6, margin: '8px 0 0' }}>
          Four slides sized for LinkedIn. Post the PDF as a document for a swipeable carousel, or the cover image on its own.
        </p>
      </div>

      <div ref={frameRef} style={{ position: 'relative', width: '100%', maxWidth: 440, aspectRatio: `${W} / ${H}`, borderRadius: 14, overflow: 'hidden', boxShadow: '0 24px 60px rgba(0,0,0,0.35)', background: art.bg }}>
        <div style={{ width: W, height: H, transform: `scale(${scale})`, transformOrigin: 'top left' }}>
          {slides[slide]}
        </div>
        <button aria-label="Previous slide" onClick={() => go(slide - 1)} style={{ ...arrow, left: 10 }}>‹</button>
        <button aria-label="Next slide" onClick={() => go(slide + 1)} style={{ ...arrow, right: 10 }}>›</button>
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        {slides.map((_, i) => (
          <button key={i} aria-label={`Slide ${i + 1}`} onClick={() => go(i)} style={{
            width: slide === i ? 24 : 8, height: 8, borderRadius: 4, border: 'none', padding: 0, cursor: 'pointer',
            background: slide === i ? art.accent : (T?.b2 || 'rgba(127,127,127,0.4)'), transition: 'width .25s ease',
          }} />
        ))}
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center', marginTop: 26 }}>
        <button onClick={downloadPDF} style={btn(true)}>{busy === 'pdf' ? 'Preparing PDF...' : 'Download carousel (PDF)'}</button>
        <button onClick={downloadCover} style={btn(false)}>{busy === 'png' ? 'Preparing image...' : 'Download cover (PNG)'}</button>
      </div>

      {/* Full-size copies used only for export */}
      <div ref={exportRef} aria-hidden="true" style={{ position: 'fixed', left: -20000, top: 0, pointerEvents: 'none' }}>
        {slides.map((s, i) => <div key={i}>{s}</div>)}
      </div>
    </div>
  );
}
